import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Flag from "react-flagkit";
import { getCountries } from "../../../utils/country.utils";

const AddressModal = ({ isOpen, onClose, onSubmit, address, isLoading, mode }) => {
    const [formData, setFormData] = useState({
        legalFirstname: '',
        legalLastname: '',
        street: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
        isoCountry: ''
    });

    const [errors, setErrors] = useState({});
    const [hasLegalRepresentative, setHasLegalRepresentative] = useState(false);

    useEffect((event) => {
        if (address) {
            setFormData({
                legalFirstname: address.legalFirstname || '',
                legalLastname: address.legalLastname || '',
                street: address.street || '',
                city: address.city || '',
                state: address.state || '',
                postcode: address.postcode || '',
                country: address.country || '',
                isoCountry: address.isoCountry || ''
            });
            // Si nous avons des données de représentant légal, cocher la case
            setHasLegalRepresentative(!!(address.legalFirstname || address.legalLastname));
        }
    }, [address]);

    const handleResetForm = () => {
        setFormData({
            legalFirstname: '',
            legalLastname: '',
            street: '',
            city: '',
            state: '',
            postcode: '',
            country: '',
            isoCountry: ''
        });
    };
    // Gestion de la touche Escape pour fermer le modal
    useEffect(() => {
        const handleEscape = (event) => {
            event.stopPropagation(); // Empêche le modal parent de gérer l'événement aussi
            if (event.key === 'Escape') {
                handleResetForm();
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCountryChange = (value) => {
        setFormData(prev => ({
            ...prev,
            country: value.name,
            isoCountry: value.iso
        }));
        // Clear errors for both country fields
        setErrors(prev => ({
            ...prev,
            country: '',
            isoCountry: ''
        }));
    };

    const toggleLegalRepresentative = () => {
        setHasLegalRepresentative(!hasLegalRepresentative);
        if (!hasLegalRepresentative) {
            // Si on active, garder les valeurs existantes
        } else {
            // Si on désactive, effacer les valeurs
            setFormData(prev => ({
                ...prev,
                legalFirstname: '',
                legalLastname: ''
            }));
            // Effacer aussi les erreurs liées
            setErrors(prev => ({
                ...prev,
                legalFirstname: '',
                legalLastname: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['street', 'city', 'postcode', 'country', 'isoCountry'];

        // Ajouter les champs du représentant légal si la case est cochée
        if (hasLegalRepresentative) {
            requiredFields.push('legalFirstname', 'legalLastname');
        }

        requiredFields.forEach(field => {
            if (!formData[field]?.trim()) {
                newErrors[field] = 'Ce champ est requis';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (validateForm()) {
            const submitData = {
                ...formData,
                // Si pas de représentant légal, envoyer null pour ces champs
                ...(hasLegalRepresentative ? {} : { legalFirstname: null, legalLastname: null }),
                ...(address?.id && { id: address.id }),
                ...(address?.isPrimary && { isPrimary: address.isPrimary })
            };
            onSubmit(submitData);
            handleResetForm();
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={() => {
                    handleResetForm();
                    onClose();
                }}
            />
            <div className="fixed z-[101] overflow-y-auto max-h-[90vh]">
                <div className="flex min-h-full items-center justify-center p-4">
                    <div
                        className="relative bg-white rounded-lg shadow-xl max-w-xl w-full"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <h3 className="font-bold text-lg mb-4 flex justify-between items-center">
                                {mode === 'create' ? 'Ajouter une adresse' : 'Modifier l\'adresse'}
                                <button
                                    type='button'
                                    className="btn btn-sm btn-circle btn-ghost"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResetForm();
                                        onClose();
                                    }}
                                >
                                    ✕
                                </button>
                            </h3>
                            <form id="address-form" onSubmit={handleSubmit} className="space-y-4">
                                {/* Checkbox pour Représentant Légal */}
                                <div className="form-control">
                                    <label className="label cursor-pointer justify-start gap-2">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={hasLegalRepresentative}
                                            onChange={toggleLegalRepresentative}
                                        />
                                        <span className="label-text">Représentant Légal</span>
                                    </label>
                                </div>

                                {/* Champs nom et prénom conditionnels */}
                                {hasLegalRepresentative && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Prénom</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="legalFirstname"
                                                value={formData.legalFirstname}
                                                onChange={handleChange}
                                                className={`input input-bordered w-full ${errors.legalFirstname ? 'input-error' : ''}`}
                                            />
                                            {errors.legalFirstname && (
                                                <label className="label">
                                                    <span className="label-text-alt text-error">{errors.legalFirstname}</span>
                                                </label>
                                            )}
                                        </div>

                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Nom</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="legalLastname"
                                                value={formData.legalLastname}
                                                onChange={handleChange}
                                                className={`input input-bordered w-full ${errors.legalLastname ? 'input-error' : ''}`}
                                            />
                                            {errors.legalLastname && (
                                                <label className="label">
                                                    <span className="label-text-alt text-error">{errors.legalLastname}</span>
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Rue</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="street"
                                        value={formData.street}
                                        onChange={handleChange}
                                        className={`input input-bordered w-full ${errors.street ? 'input-error' : ''}`}
                                    />
                                    {errors.street && (
                                        <label className="label">
                                            <span className="label-text-alt text-error">{errors.street}</span>
                                        </label>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Ville</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            className={`input input-bordered w-full ${errors.city ? 'input-error' : ''}`}
                                        />
                                        {errors.city && (
                                            <label className="label">
                                                <span className="label-text-alt text-error">{errors.city}</span>
                                            </label>
                                        )}
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Code postal</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="postcode"
                                            value={formData.postcode}
                                            onChange={handleChange}
                                            className={`input input-bordered w-full ${errors.postcode ? 'input-error' : ''}`}
                                        />
                                        {errors.postcode && (
                                            <label className="label">
                                                <span className="label-text-alt text-error">{errors.postcode}</span>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Pays</span>
                                    </label>
                                    <div className={errors.country || errors.isoCountry ? 'border border-error rounded-lg' : ''}>
                                        <CustomCountrySelector 
                                            value={{ name: formData.country, iso: formData.isoCountry }}
                                            onChange={handleCountryChange}
                                            placeholder="Sélectionner un pays"
                                        />
                                    </div>
                                    {(errors.country || errors.isoCountry) && (
                                        <label className="label">
                                            <span className="label-text-alt text-error">Le pays est requis</span>
                                        </label>
                                    )}
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">État/Région</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        className={`input input-bordered w-full ${errors.state ? 'input-error' : ''}`}
                                    />
                                    {errors.state && (
                                        <label className="label">
                                            <span className="label-text-alt text-error">{errors.state}</span>
                                        </label>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-2 mt-6">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetForm();
                                            onClose();
                                        }}
                                        disabled={isLoading}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                        form='address-form'
                                    >
                                        {isLoading
                                            ? (mode === 'create' ? 'Création...' : 'Modification...')
                                            : (mode === 'create' ? 'Créer' : 'Modifier')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById('root')
    );
};

// Composant adapté de CountryInput sans dépendance à Formik
const CustomCountrySelector = ({ value, onChange, placeholder, disabled = false }) => {
    const countries = getCountries();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const selectedCountry = value?.iso 
        ? countries.find((country) => country.iso === value.iso)
        : null;

    const handleCountrySelect = (country) => {
        if (!disabled) {
            onChange(country);
            setDropdownOpen(false);
        }
    };

    const filteredCountries = countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="relative w-full">
            <div
                className={`flex flex-row items-center border cursor-pointer p-2 bg-base-100 border-base-300 rounded-lg input hover:border-gray-400 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={() => !disabled && setDropdownOpen((prev) => !prev)}
            >
                <Flag country={selectedCountry?.iso || "FR"} size={24} className="flex-shrink-0" />
                <span className="ml-2">{selectedCountry?.name || placeholder || "Select a country"}</span>
            </div>
            {dropdownOpen && (
                <div className="absolute z-10 bg-white border rounded shadow mt-1 w-full max-h-52 overflow-y-auto">
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        className="w-full p-2 border-b"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={disabled}
                    />
                    <ul>
                        {filteredCountries.map((country) => (
                            <li
                                key={country.iso}
                                onClick={() => handleCountrySelect(country)}
                                className="flex items-center gap-2 p-2 cursor-pointer z-10 hover:bg-gray-100"
                            >
                                <Flag country={country.iso} size={24} className="flex-shrink-0" /> 
                                <span className="ml-2">{country.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AddressModal; 